import React, { useCallback, useState } from 'react';
import SeloDeComentarios from '../../../components/comentarios/SeloDeComentarios';

import {
  FiltrosCompras,
  FiltrosDaLista,
  RecorteDeRequisicao,
  fetchRequisicoesDoRecorte,
} from '../../../api/comprasDashboardApi';
import { SITUACOES_DE_REQUISICAO, rotuloDaSituacao } from '../situacoes';
import ModalDeDetalhe, { SUFIXO_FILTRADO, useDetalhePaginado } from './ModalDeDetalhe';
import DetalheDaRequisicaoModal from './DetalheDaRequisicaoModal';
// at-113: o selo da salvaguarda. Paleta compartilhada — nunca classe de status própria por tela.
import '../../../shared/ui/badge-status.css';

/**
 * O detalhamento de um card do painel de Compras: QUAIS requisições compõem aquele número.
 *
 * Existe porque o card sozinho não sustenta decisão — "65 aguardando atendimento" não diz quais,
 * nem há quanto tempo, nem de qual oficina (pedido do Victor, 11/09/2026).
 *
 * 🔴 O recorte é resolvido pelo SERVIDOR, pelo mesmo `where` que produziu o número
 * (`whereDoRecorte`). A tela manda um id de recorte, nunca um filtro montado aqui — se ela
 * montasse, "Aguardando atendimento" (que é `pendente` E sem comprador) abriria com as 142
 * pendentes em vez das 65, e ninguém saberia qual dos dois números está errado.
 *
 * 🔴 Falha de carga NUNCA vira tabela vazia: "este card não tem requisição nenhuma" é a conclusão
 * oposta à verdadeira, e é exatamente o que um catch silencioso produziria (regra da at-60). Quem
 * garante isso é o casco compartilhado (`ModalDeDetalhe` + `useDetalhePaginado`), sobre o qual este
 * modal monta desde a at-95.
 */

const PORTAL_PAGINA = 25;

export interface RecorteAberto {
  /**
   * O id do recorte, quando o clique veio de um card do funil ou dos KPIs.
   *
   * ⚠️ **Opcional desde a at-110**: os detalhamentos de comprador e de oficina não são recortes
   * fechados — eles são o filtro do painel mais um atributo (`comprador`, `oficina`, `situacao`),
   * que é exatamente o `where` que produziu aquela linha. Inventar um recorte novo no enum para
   * cada comprador da lista não faria sentido.
   */
  recorte?: RecorteDeRequisicao;
  /**
   * Os filtros que definem o recorte quando não há `recorte` — somados aos do painel.
   *
   * 🔴 Precisam ser os MESMOS que o card usou para contar. É por isso que `comprador` e `oficina`
   * entram aqui e não são montados dentro do modal: quem sabe qual linha foi clicada é quem a
   * renderizou.
   */
  filtrosExtras?: {
    comprador?: string;
    oficina?: string;
    placa?: string;
    /** at-112: multi-seleção, vinda do filtro do card que abriu a lista. */
    situacoes?: string[];
    /** at-112: o centro de custo da linha clicada, ou SEM_CC. */
    centrosCusto?: string[];
    /** at-111: quem pediu. */
    solicitante?: string;
  };
  titulo: string;
  /*
   * 🔴 NÃO existe `totalDoCard` aqui, e a ausência é deliberada (decisão do Victor, 11/09/2026).
   *
   * A versão anterior recebia o número do card para confrontá-lo com o do servidor e avisar quando
   * discordassem. Aquele aviso era a confissão de que o sistema pode mentir: ele transferia ao
   * usuário o trabalho de desconfiar, em vez de tornar a divergência impossível.
   *
   * O acordo é garantido na origem — `whereDoRecorte` é a definição ÚNICA de cada card, usada para
   * contar e para listar — e afirmado por sentinela. Divergência é falha de teste, não texto de
   * tela.
   */
  /** Texto do próprio card ("já com comprador"), para o modal não perder o contexto do clique. */
  legenda?: string;
}

interface Props {
  aberto: RecorteAberto;
  filtros: FiltrosCompras;
  onFechar: () => void;
}

/**
 * 🔴 Quatro colunas, não dez (at-110, pedido do Victor: "preciso dela mais concisa").
 *
 * O que ficou é o que serve para **varrer a lista**: qual requisição é, quando entrou, em que pé
 * está e o tamanho dela. Comprador, solicitante, oficina, placa, local e justificativa saíram
 * para o **detalhe**, a um clique no número — na tabela eles viravam dez colunas de reticências,
 * e o dado ficava ilegível justamente por estar todo ali.
 *
 * 🔴 **at-117 devolveu DUAS delas — placa e oficina** (decisão do Victor, 27/09/2026), e isso
 * reabre a at-110 conscientemente. O motivo é de uso: a Alessandra abre a lista a partir de uma
 * oficina e precisa saber de qual veículo é cada requisição, sem entrar no detalhe uma a uma.
 * Passaram a ser seis; as outras quatro seguem no detalhe.
 *
 * ⚠️ Nenhuma mudança de API — o backend já mandava `placa` e `oficina` na listagem desde a
 * at-109. Os dois vêm do texto da justificativa e faltam em parte da base: célula vazia é estado
 * legítimo, exibido como traço.
 *
 * 🔴 **A coluna "Dias" SAIU na at-112** (decisão do Victor, 22/09/2026: "de fato não seria
 * confiável"). Ela contava do cadastro até HOJE, em toda linha — inclusive nas já atendidas e
 * canceladas —, então numa requisição atendida no ano passado exibia "há 400 dias", que não é
 * espera nenhuma: é a idade do registro.
 *
 * ⚠️ **A métrica certa — do cadastro até o pedido ser gerado — não existe, e não é esquecimento.**
 * O Nasajon não guarda data de mudança de situação; o espelho só sabe as transições que ele mesmo
 * observou, desde 11/09/2026. Medido em 22/09/2026: das 1.559 requisições em "pedido gerado",
 * apenas **157** têm a data real (mediana de 4 dias). Nas outras 1.402 a única data disponível é a
 * da primeira varredura, que exibida como tempo de atendimento seria número fabricado. Há e-mail
 * aberto com a Nasajon pedindo o histórico de situações; quando ele existir, a coluna volta.
 */
const COLUNAS = ['Requisição', 'Cadastrada em', 'Situação', 'Placa', 'Oficina', 'Itens'];

/**
 * at-119 — a coluna "Dias na fila" VOLTOU, só na lista que o KPI "Tempo na fila de Compras" abre
 * (recorte `pendentes`; pedido do Victor, 02/10/2026).
 *
 * 🔴 Não é a coluna "Dias" que a at-112 removeu: aquela contava do cadastro até hoje em TODA
 * linha, inclusive atendidas e canceladas. Esta só existe onde toda linha está na fila — e o
 * número vem do SERVIDOR (`diasNaFila`), pela mesma conta do KPI. A tela não calcula `hoje −
 * cadastro` por conta própria, senão o número da linha e o do topo divergiriam no fuso do
 * navegador (a armadilha do `new Date(ano, mes, dia)` da at-100).
 */
/*
 * at-120 — "Fila" diz se a requisição ainda está na fila (os dias contam até hoje) ou já virou
 * pedido (contam até a emissão do primeiro pedido). Sugerido pelo Victor, 05/10/2026: o KPI mede as
 * duas juntas, e a coluna é o que separa na lista.
 */
const COLUNAS_DA_FILA = ['Requisição', 'Cadastrada em', 'Dias na fila', 'Fila', 'Situação', 'Placa', 'Oficina', 'Itens'];

const ROTULO_DA_FILA = { EM_ABERTO: 'Em aberto', VIROU_PEDIDO: 'Virou pedido' } as const;

function formatarData(iso: string | null): string {
  if (!iso) return '-';
  return new Date(iso).toLocaleDateString('pt-BR');
}

const CAMPOS_DA_LISTA = ['busca', 'situacoes', 'placa', 'oficina', 'comprador', 'solicitante', 'periodo'] as const;

// O vocabulário do ERP não é o da tela, e ele vive num lugar só desde a at-110 (`situacoes.ts`):
// eram duas cópias, e o menu de filtro seria a terceira.

export default function RequisicoesDoRecorteModal({ aberto, filtros, onFechar }: Props) {
  // at-110: a requisição aberta em detalhe. Montagem condicional (padrão do projeto), para o modal
  // filho recomeçar do zero a cada abertura.
  const [requisicaoAberta, setRequisicaoAberta] = useState<{ id: string; codigo: string } | null>(null);

  const carregarDados = useCallback(
    (pagina: number, daLista: FiltrosDaLista) =>
      fetchRequisicoesDoRecorte({
        // Os filtros do painel MAIS o atributo da linha clicada (at-110). Os extras vêm depois de
        // propósito: um clique numa linha de comprador vence o comprador que estivesse no filtro
        // geral, senão a lista seria a interseção de dois recortes diferentes.
        filtros: { ...filtros, ...aberto.filtrosExtras },
        recorte: aberto.recorte,
        daLista,
        pagina,
        porPagina: PORTAL_PAGINA,
      }),
    [aberto.recorte, aberto.filtrosExtras, filtros],
  );
  const { dados, carregando, erro, pagina, setPagina, filtrosDaLista, aplicarFiltrosDaLista, filtrado } =
    useDetalhePaginado(carregarDados);

  const total = dados?.total ?? 0;
  const requisicoes = dados?.requisicoes ?? [];
  const daFila = aberto.recorte === 'pendentes';

  return (
    <>
      <ModalDeDetalhe
        titulo={aberto.titulo}
        legenda={
          <>
            {carregando
              ? 'Carregando...'
              : erro
                ? 'Não foi possível carregar'
                : `${total.toLocaleString('pt-BR')} ${total === 1 ? 'requisição' : 'requisições'}`}
            {aberto.legenda ? ` · ${aberto.legenda}` : ''}
            {filtrado ? SUFIXO_FILTRADO : ''}
          </>
        }
        carregando={carregando}
        erro={erro}
        erroTexto="Não foi possível carregar as requisições deste card. Isto não significa que ele esteja vazio — tente de novo e, se persistir, acione a TI."
        /*
          🔴 at-115 — o recorte vazio POR CONSTRUÇÃO sob os filtros em vigor (`indisponivel`) não é a
          mesma coisa que não haver requisições: a explicação vem do servidor. A tela normalmente nem
          oferece este clique, mas a rota é alcançável por URL e pelo frontend anterior.
        */
        vazio={Boolean(dados?.indisponivel) || requisicoes.length === 0}
        vazioTexto={
          dados?.indisponivel
            ? dados.indisponivel.explicacao
            : filtrado
              ? 'Nenhuma requisição deste recorte com os filtros da lista. Ajuste ou limpe os filtros.'
              : 'Nenhuma requisição neste recorte.'
        }
        colunas={daFila ? COLUNAS_DA_FILA : COLUNAS}
        classeTabela={`dcr-tabela-requisicoes${daFila ? ' dcr-tabela-fila' : ''}`}
        paginacao={{ pagina, ultimaPagina: Math.max(1, Math.ceil(total / PORTAL_PAGINA)), onMudar: setPagina }}
        travarFechamento={Boolean(requisicaoAberta)}
        filtroDaLista={{
          campos: [...CAMPOS_DA_LISTA],
          situacoes: SITUACOES_DE_REQUISICAO,
          rotuloDaBusca: 'número, placa, oficina, comprador ou texto da justificativa',
          rotuloDoPeriodo: 'Cadastro da requisição',
          aplicados: filtrosDaLista,
          onAplicar: aplicarFiltrosDaLista,
        }}
        onFechar={onFechar}
      >
        {requisicoes.map((r) => {
          return (
            <div key={r.id} className="dcr-linha">
              <div className="dcr-celula" title={r.codigo}>
                {/*
                  O número abre o detalhe: comprador, solicitante, oficina, placa, local, a
                  justificativa inteira e os ITENS. `button` de verdade, como o
                  `do-celula-link` das tabelas do painel — alcançável por teclado e
                  anunciado como algo que abre.
                */}
                <button
                  type="button"
                  className="dcr-celula-link"
                  onClick={() => setRequisicaoAberta({ id: r.id, codigo: r.codigo })}
                >
                  {r.codigo}
                </button>
                <SeloDeComentarios total={r.comentarios} />
                {/*
                  Entrou pela salvaguarda do card de oficinas (at-113): a classificação
                  financeira dela não é de compra de peça, e ela está na lista porque tem
                  oficina preenchida.

                  🔴 É aqui que o dado vira ACIONÁVEL: o `title` diz QUAL classificação
                  está de fora, que é o que alguém leva para a tela de configuração. No
                  card só se vê que existe mistura; aqui se vê de quê.
                */}
                {r.foraDaLista ? (
                  <span
                    className="cilia-badge badge-cinza"
                    title={
                      r.classificacaoFinanceira
                        ? `Classificação "${r.classificacaoFinanceira}" não está na lista de compra de peça. Esta requisição entrou porque tem oficina preenchida.`
                        : 'Sem classificação financeira cadastrada. Esta requisição entrou porque tem oficina preenchida.'
                    }
                  >
                    Fora da lista
                  </span>
                ) : null}
              </div>
              <div className="dcr-celula">{formatarData(r.dataCadastro)}</div>
              {/*
                at-119 — só na lista da fila. O traço é o caso em que o servidor não tem
                a data de cadastro; fora da fila a coluna nem é montada.
              */}
              {daFila ? (
                <>
                  <div className="dcr-celula">{r.diasNaFila == null ? '—' : `${r.diasNaFila} d`}</div>
                  <div className="dcr-celula">
                    {r.filaSituacao ? (
                      <span
                        className={`cilia-badge ${r.filaSituacao === 'EM_ABERTO' ? 'badge-laranja' : 'badge-verde'}`}
                        title={
                          r.filaSituacao === 'EM_ABERTO'
                            ? 'Ainda na fila: os dias contam até hoje.'
                            : 'Já virou pedido: os dias contam até a emissão do primeiro pedido.'
                        }
                      >
                        {ROTULO_DA_FILA[r.filaSituacao]}
                      </span>
                    ) : (
                      '—'
                    )}
                  </div>
                </>
              ) : null}
              <div className="dcr-celula" title={r.situacao ?? ''}>
                {rotuloDaSituacao(r.situacao)}
              </div>
              {/*
                at-117 — placa e oficina voltaram para a lista (decisão do Victor,
                27/09/2026; pedido da Alessandra: ao abrir uma oficina ela precisa ver de
                qual veículo é cada requisição).

                ⚠️ Nenhuma mudança de API: o backend já mandava os dois campos desde a
                at-109.

                ⚠️ Os dois vêm do TEXTO da justificativa e faltam em parte da base — o
                traço é estado legítimo, não erro. A oficina é texto livre, então a célula
                trunca numa linha só com o valor inteiro no `title` (regra de 12/08/2026).
              */}
              <div className="dcr-celula" title={r.placa ?? ''}>
                {r.placa ?? '—'}
              </div>
              <div className="dcr-celula" title={r.oficina ?? ''}>
                {r.oficina ?? '—'}
              </div>
              {/* Quantos itens o clique vai abrir — é o que motiva o clique. */}
              <div className="dcr-celula">{r.itens}</div>
            </div>
          );
        })}
      </ModalDeDetalhe>

      {requisicaoAberta && (
        <DetalheDaRequisicaoModal
          requisicaoId={requisicaoAberta.id}
          codigo={requisicaoAberta.codigo}
          onFechar={() => setRequisicaoAberta(null)}
        />
      )}
    </>
  );
}
